<template>
  <v-card flat>
    <v-card-text>
      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="3">
          <v-card color="indigo darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.grand_totals.total_categories }}
            </div>
            <div class="caption">Total Categories</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatNumber(report.grand_totals.total_quantity) }}
            </div>
            <div class="caption">Total Stock Qty</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="teal darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_totals.total_cost_value) }}
            </div>
            <div class="caption">Total Cost Value</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="green darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_totals.total_potential_profit) }}
            </div>
            <div class="caption">Potential Profit</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Additional Summary -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold">
              {{ report.grand_totals.total_items }}
            </div>
            <div class="caption grey--text">Total Items</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold">
              {{ formatCurrency(report.grand_totals.total_retail_value) }}
            </div>
            <div class="caption grey--text">Total Retail Value</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold red--text">
              {{ report.grand_totals.total_out_of_stock }}
            </div>
            <div class="caption grey--text">Out of Stock</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold orange--text">
              {{ report.grand_totals.total_low_stock }}
            </div>
            <div class="caption grey--text">Low Stock</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="report.categories"
        :headers="headers"
        :search="search"
        :expanded.sync="expanded"
        item-key="category"
        show-expand
        dense
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
        @click:row="toggleRow"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search categories..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:item.cost_value="{ item }">
          {{ formatCurrency(item.cost_value) }}
        </template>
        <template v-slot:item.retail_value="{ item }">
          {{ formatCurrency(item.retail_value) }}
        </template>
        <template v-slot:item.potential_profit="{ item }">
          <span
            :class="item.potential_profit >= 0 ? 'green--text' : 'red--text'"
          >
            {{ formatCurrency(item.potential_profit) }}
          </span>
        </template>
        <template v-slot:item.avg_cost_per_unit="{ item }">
          {{ formatCurrency(item.avg_cost_per_unit) }}
        </template>
        <template v-slot:item.out_of_stock_count="{ item }">
          <v-chip v-if="item.out_of_stock_count > 0" color="red" dark x-small>
            {{ item.out_of_stock_count }}
          </v-chip>
          <span v-else class="grey--text">0</span>
        </template>
        <template v-slot:item.low_stock_count="{ item }">
          <v-chip v-if="item.low_stock_count > 0" color="orange" dark x-small>
            {{ item.low_stock_count }}
          </v-chip>
          <span v-else class="grey--text">0</span>
        </template>

        <!-- Expanded Row: Sub-types and items within this category -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <!-- Sub-types section -->
            <div v-if="item.sub_types && item.sub_types.length > 0">
              <div
                v-for="st in item.sub_types"
                :key="st.sub_type"
                class="sub-type-group"
              >
                <!-- Sub-type header row (clickable to toggle items) -->
                <div
                  class="sub-type-header d-flex align-center px-4 py-2"
                  @click="toggleSubType(item.category, st.sub_type)"
                  style="cursor: pointer"
                >
                  <v-icon small class="mr-2">
                    {{
                      isSubTypeExpanded(item.category, st.sub_type)
                        ? "mdi-chevron-down"
                        : "mdi-chevron-right"
                    }}
                  </v-icon>
                  <span
                    class="
                      font-weight-bold
                      text-body-2
                      indigo--text
                      text--darken-2
                    "
                    >{{ st.sub_type }}</span
                  >
                  <v-chip x-small outlined class="ml-3 mr-2"
                    >{{ st.item_count }} items</v-chip
                  >
                  <span class="caption grey--text mr-3"
                    >Qty: {{ st.total_quantity }}</span
                  >
                  <span class="caption grey--text mr-3"
                    >Cost: {{ formatCurrency(st.cost_value) }}</span
                  >
                  <span class="caption grey--text mr-3"
                    >Retail: {{ formatCurrency(st.retail_value) }}</span
                  >
                  <span
                    class="caption mr-3"
                    :class="
                      st.potential_profit >= 0 ? 'green--text' : 'red--text'
                    "
                  >
                    Profit: {{ formatCurrency(st.potential_profit) }}
                  </span>
                  <v-spacer></v-spacer>
                  <v-chip
                    v-if="st.out_of_stock_count > 0"
                    color="red"
                    dark
                    x-small
                    class="mr-1"
                  >
                    {{ st.out_of_stock_count }} OOS
                  </v-chip>
                  <v-chip
                    v-if="st.low_stock_count > 0"
                    color="orange"
                    dark
                    x-small
                  >
                    {{ st.low_stock_count }} Low
                  </v-chip>
                </div>

                <!-- Sub-type items (shown when sub-type is expanded) -->
                <v-simple-table
                  v-if="isSubTypeExpanded(item.category, st.sub_type)"
                  dense
                  class="expanded-items-table ml-6"
                >
                  <thead>
                    <tr class="blue-grey lighten-5">
                      <th>Item Name</th>
                      <th>Supplier</th>
                      <th class="text-center">Qty</th>
                      <th class="text-end">Unit Cost</th>
                      <th class="text-end">Selling Price</th>
                      <th class="text-end">Cost Value</th>
                      <th class="text-end">Retail Value</th>
                      <th class="text-end">Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="child in st.items"
                      :key="child.id"
                      :class="getItemRowClass(child)"
                    >
                      <td>{{ child.item_name }}</td>
                      <td class="grey--text text--darken-1">
                        {{ child.supplier || "-" }}
                      </td>
                      <td class="text-center">
                        <v-chip
                          :color="
                            child.quantity <= 0
                              ? 'red'
                              : child.reorder_point > 0 &&
                                child.quantity <= child.reorder_point
                              ? 'orange'
                              : 'green'
                          "
                          dark
                          x-small
                        >
                          {{ child.quantity }}
                        </v-chip>
                      </td>
                      <td class="text-end">
                        {{ formatCurrency(child.unit_price) }}
                      </td>
                      <td class="text-end">
                        {{ formatCurrency(child.selling_price) }}
                      </td>
                      <td class="text-end">
                        {{ formatCurrency(child.cost_value) }}
                      </td>
                      <td class="text-end">
                        {{ formatCurrency(child.retail_value) }}
                      </td>
                      <td class="text-end">
                        <span
                          :class="
                            child.profit >= 0 ? 'green--text' : 'red--text'
                          "
                        >
                          {{ formatCurrency(child.profit) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </div>

            <!-- Uncategorized items (no sub-type) -->
            <div
              v-if="
                item.uncategorized_items && item.uncategorized_items.length > 0
              "
            >
              <div
                v-if="item.sub_types && item.sub_types.length > 0"
                class="sub-type-header d-flex align-center px-4 py-2"
                @click="toggleSubType(item.category, '__uncategorized__')"
                style="cursor: pointer"
              >
                <v-icon small class="mr-2">
                  {{
                    isSubTypeExpanded(item.category, "__uncategorized__")
                      ? "mdi-chevron-down"
                      : "mdi-chevron-right"
                  }}
                </v-icon>
                <span
                  class="font-weight-bold text-body-2 grey--text text--darken-1"
                  >No Sub-Type</span
                >
                <v-chip x-small outlined class="ml-3"
                  >{{ item.uncategorized_items.length }} items</v-chip
                >
              </div>

              <v-simple-table
                v-if="
                  !item.sub_types ||
                  item.sub_types.length === 0 ||
                  isSubTypeExpanded(item.category, '__uncategorized__')
                "
                dense
                class="expanded-items-table"
                :class="{ 'ml-6': item.sub_types && item.sub_types.length > 0 }"
              >
                <thead>
                  <tr class="grey lighten-4">
                    <th>Item Name</th>
                    <th>Supplier</th>
                    <th class="text-center">Qty</th>
                    <th class="text-end">Unit Cost</th>
                    <th class="text-end">Selling Price</th>
                    <th class="text-end">Cost Value</th>
                    <th class="text-end">Retail Value</th>
                    <th class="text-end">Profit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="child in item.uncategorized_items"
                    :key="child.id"
                    :class="getItemRowClass(child)"
                  >
                    <td>{{ child.item_name }}</td>
                    <td class="grey--text text--darken-1">
                      {{ child.supplier || "-" }}
                    </td>
                    <td class="text-center">
                      <v-chip
                        :color="
                          child.quantity <= 0
                            ? 'red'
                            : child.reorder_point > 0 &&
                              child.quantity <= child.reorder_point
                            ? 'orange'
                            : 'green'
                        "
                        dark
                        x-small
                      >
                        {{ child.quantity }}
                      </v-chip>
                    </td>
                    <td class="text-end">
                      {{ formatCurrency(child.unit_price) }}
                    </td>
                    <td class="text-end">
                      {{ formatCurrency(child.selling_price) }}
                    </td>
                    <td class="text-end">
                      {{ formatCurrency(child.cost_value) }}
                    </td>
                    <td class="text-end">
                      {{ formatCurrency(child.retail_value) }}
                    </td>
                    <td class="text-end">
                      <span
                        :class="child.profit >= 0 ? 'green--text' : 'red--text'"
                      >
                        {{ formatCurrency(child.profit) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </td>
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1">mdi-chart-box-outline</v-icon>
        <p class="mt-4 subtitle-1">
          Click "Generate" to view stock position by category.
        </p>
      </div>

      <!-- No Results -->
      <div
        v-if="report && report.categories && report.categories.length === 0"
        class="text-center pa-10"
      >
        <v-icon size="64" color="grey">mdi-package-variant</v-icon>
        <p class="mt-4 subtitle-1 grey--text">
          No items found matching the current filters.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "StockPositionReport",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      expanded: [],
      expandedSubTypes: {},
      headers: [
        { text: "", value: "data-table-expand" },
        { text: "Category", value: "category" },
        { text: "Items", value: "item_count", align: "center" },
        { text: "Total Qty", value: "total_quantity", align: "center" },
        { text: "Cost Value", value: "cost_value", align: "end" },
        { text: "Retail Value", value: "retail_value", align: "end" },
        { text: "Potential Profit", value: "potential_profit", align: "end" },
        { text: "Avg Cost/Unit", value: "avg_cost_per_unit", align: "end" },
        { text: "Out of Stock", value: "out_of_stock_count", align: "center" },
        { text: "Low Stock", value: "low_stock_count", align: "center" },
      ],
    };
  },
  methods: {
    ...mapActions({
      fetchReport: "inventory_reports/get_stock_position_report",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatNumber(value) {
      if (!value && value !== 0) return "0";
      return Number(value).toLocaleString("en-PH");
    },
    toggleRow(item) {
      const index = this.expanded.findIndex(
        (e) => e.category === item.category
      );
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(item);
      }
    },
    toggleSubType(category, subType) {
      const key = category + "::" + subType;
      this.$set(this.expandedSubTypes, key, !this.expandedSubTypes[key]);
    },
    isSubTypeExpanded(category, subType) {
      const key = category + "::" + subType;
      return !!this.expandedSubTypes[key];
    },
    getItemRowClass(child) {
      if (child.quantity <= 0) return "red lighten-5";
      if (child.reorder_point > 0 && child.quantity <= child.reorder_point)
        return "orange lighten-5";
      return "";
    },
    async generate() {
      try {
        let params = {};
        if (this.filters.item_type_id)
          params.item_type_id = this.filters.item_type_id;
        if (this.filters.supplier) params.supplier = this.filters.supplier;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        this.expanded = [];
        this.expandedSubTypes = {};
        this.report = await this.fetchReport(params);
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating stock position report: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>

<style scoped>
.expanded-items-table {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}
.expanded-items-table th {
  font-size: 11px !important;
}
.expanded-items-table td {
  font-size: 12px !important;
}
.sub-type-group {
  border-bottom: 1px solid #eeeeee;
}
.sub-type-group:last-child {
  border-bottom: none;
}
.sub-type-header {
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}
.sub-type-header:hover {
  background: #eeeeee;
}
</style>
